/**
	* Overview map view
	* Handles: overview map
	* @module OverviewMap
*/

/// Imports

// React
import { useState, useEffect } from "react";

// jQuery
import $ from "jquery";

// OpenLayers
import OlMap from "ol/Map";

// Tools
import { assetURL } from "lib/utilities/asset";

// Lang
import { getLocaleString } from "lib/localization/vendor";

// Styles
import "styles/css/OverviewMap.scss";

interface OverviewMapProps {
	olMap:OlMap,
	overviewMap:OlMap
}

const offsetZoom:number = 5;
var resizeTimer:any;
var firstRun:boolean = true;

export default function OverviewMap({olMap, overviewMap}:OverviewMapProps) {
	if(!overviewMap) return;
	
	const mapConfig:any = olMap.get("opus-config");
	
	const [rightPanelOpen, setRightPanelOpen] = useState<boolean>(false);
	const [minimized, setMinimized] = useState<boolean>(false);
	const [markerShowing, setMarkerShowing] = useState<boolean>(false);
	const [baseAttribShowing, setBaseAttribShowing] = useState<boolean>(null);
	
	// Initialize after first draw
	useEffect(() => {
		// Right panel visible show/hide the overview map
		_events.on("right-panel-update", (open:boolean) => {
			setTimeout(() => setRightPanelOpen(open), 100);
		});
		
		// Marker showing event
		_events.on("marker-showing", (showing:boolean) => {
			setTimeout(() => setMarkerShowing(showing), 100);
		});
		
		// Base layer attribution trigger
		_events.on("base-attribution-showing", (showing:boolean) => {
			setTimeout(() => setBaseAttribShowing(showing), 100);
		});
		
		// Set this value to false so that it always draws the overview map after first load
		firstRun = false;
	}, []);
	
	// Setup overview map
	useEffect(() => {
		overviewMap.setTarget("overview-map-container");
		
		syncOverviewMap();
	}, [overviewMap]);
	
	// Setup main map overview-map related events
	useEffect(() => {
		olMap.on(["moveend", "movestart", "pointerdrag"], (e:any) => {
			syncOverviewMap();
		});
	}, [olMap]);
	
	// Call after the screen dimensions change
	useEffect(() => {
		if(!shouldShow()) {
			$("#overview-map").fadeOut(_ui.transitionUpOptions);
		}else{
			$("#overview-map").fadeIn(_ui.transitionDownOptions);
		}
		
		if(!minimized) {
			updateBbox();
		}
		
		if(resizeTimer) clearTimeout(resizeTimer);
		resizeTimer = setTimeout(() => adjust(), 100); // delayed so that the transition finishes before the re-render
	}, [_dimensions, markerShowing, baseAttribShowing, minimized, olMap.get("opus-config"), _config.lang.locale]); // config because the contents of the attribution box may change depending on locale
	
	useEffect(() => {
		_events.invoke("overview-map-minimized", minimized);
	}, [minimized]);
	
	const syncOverviewMap = () => {
		overviewMap.getView().setCenter(olMap.getView().getCenter());
		overviewMap.getView().setRotation(olMap.getView().getRotation());
		overviewMap.getView().setZoom(olMap.getView().getZoom() - offsetZoom);
		
		updateBbox();
	}
	const adjust = () => {
		overviewMap.updateSize();
		overviewMap.render();
		
		updateBbox();
		
		const attributionHeight:number = $("#attributions").height();
		
		$("#overview-map").css("bottom", attributionHeight + 30);
	}
	const updateBbox = () => {
		const overviewWidth:number = $("#overview-map").width();
		const overviewHeight:number = $("#overview-map").height();
		let zoom:number = (overviewMap.getView().getZoom() + offsetZoom) / olMap.getView().getZoom();
		const oz:number = offsetZoom + 1.5;
		
		if(zoom > 1)
			zoom *= (1 + (zoom-1)) * 2;
		
		$("#overview-map-bbox").width(_dimensions.width / overviewWidth * oz * zoom);
		$("#overview-map-bbox").height(_dimensions.height / overviewHeight * oz * zoom);
	}
	const toggleMinimize = () => {
		_ui.tooltip.hide();
		_ui.tooltip.rebuild();
		setMinimized(!minimized);
	}
	const shouldShow = ():boolean => {
		if(rightPanelOpen) {
			const caller:string = _ui.getRightPanelOpenCaller();
			const rightPanelHeight:number = $(`#${caller}`)?.height() || 0;
			const overviewHeight:number = $("#overview-map").height();
			const freeSpace:number = _dimensions.height - rightPanelHeight - overviewHeight;
			
			if(freeSpace < 100)
				return false;
		}
		
		const widthLimit:number = markerShowing ? 450 : 350;
		
		if(_dimensions.width < widthLimit || _dimensions.height < 400)
			return false;
		
		return true;
	}
	const drawNorthArrow = (smaller:boolean, minimized:boolean) => {
		let className:string = "overview-map__north-arrow";
		
		if(smaller)
			className += " overview-map__north-arrow--smaller"
		
		if(minimized)
			className += " overview-map__north-arrow--minimized"
		
		return (
			<div className={className}>
				<img src={assetURL("assets/images/northarrow.png")} />
			</div>
		)
	}
	const drawOverviewMap = () => {
		if(!overviewMap) return null;
		
		let className:string = "rect overview-map";
		let smaller:boolean = _dimensions.width < 600 || _dimensions.height < 600;
		
		if(smaller)
			className += " overview-map--smaller";
		
		if(minimized)
			className += " overview-map--minimized";
			
		let classNameBbox:string = "overview-map__bounding-box";
		
		if(minimized)
			classNameBbox += " overview-map__bounding-box--minimized";
			
		let classNameContainer:string = "overview-map__map-container";
		
		if(minimized)
			classNameContainer += " overview-map__map-container--minimized"
		
		return (
			<div id="overview-map" className={className} style={{display: (!shouldShow() && firstRun) ? "none" : ""}}>
				<div 
					id="overview-map-container" 
					className={classNameContainer} 
				></div>
				<div id="overview-map-bbox" className={classNameBbox}></div>
				<div 
					className={`overview-map__minimize-button${minimized ? " overview-map__minimize-button--minimized" : ""}`}
					data-tip={minimized ? getLocaleString("expand") : getLocaleString("minimize")}
					data-delay-show="700" 
					onClick={toggleMinimize}
				>
					<img src={assetURL("assets/images/arrowsmall.png")} />
				</div>
				{mapConfig.northArrowEnabled ? drawNorthArrow(smaller, minimized) : null}
			</div>
		)
	}
	
	return drawOverviewMap();
}